import { call, put, select, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { push } from 'connected-react-router';
import {
  GO_TO_FORGOT_PASSWORD,
  GO_TO_FORGOT_PASSWORD_NEW,
  LOAD_PASSWORD_MODE,
  passwordChangeActions,
  PROCESS_PASSWORD_RESET,
  REQUEST_PASSWORD_RESET,
  REQUEST_PASSWORD_RESET_OLD,
  REQUEST_NARWHAL_PASSWORD_RESET
} from '../reducer/password-change-reducer';
import {
  loadPasswordMode,
  processServerPasswordResetCatalyst,
  processServerPasswordResetConsumer,
  requestServerPasswordReset,
  requestServerPasswordResetOld,
  requestNarwhalPasswordReset as requestNarwhalPasswordResetService
} from '../services/password-change-helpers';
import { authActions, authSelectors } from '@rosetta/react-auth';
import { appActions, TRANSACTION_STATE } from '../../app/reducer/app-reducer';
import { emitEvent, RESET_PASSWORD } from '../../../helpers/eventingHelper';

function* requestPasswordReset(action) {
  try {
    yield call(emitEvent, RESET_PASSWORD, {
      Email: action.payload.userEmail,
      Application: 'Launchpad',
      'Application Version': '0.1.0'
    });

    let requestResult = null;
    // Send request to launchpad-service API
    yield put(appActions.setLastTransactionState(TRANSACTION_STATE.PENDING));
    yield put(appActions.setPasswordResetAccountAvailability(null));
    requestResult = yield call(
      requestServerPasswordReset,
      action.payload.userEmail,
      action.payload.workspace,
      action.payload.accountType,
      action.payload.locale
    );

    const hasAccountAvailability =
      requestResult.hasOwnProperty('has_personal_account') ||
      requestResult.hasOwnProperty('has_business_account') ||
      requestResult.hasOwnProperty('has_narwhal_account');

    if (hasAccountAvailability) {
      const availability = {
        has_personal_account: !!requestResult.has_personal_account,
        has_business_account: !!requestResult.has_business_account,
        has_narwhal_account: !!requestResult.has_narwhal_account
      };
      const availableCount = [
        availability.has_personal_account,
        availability.has_business_account,
        availability.has_narwhal_account
      ].filter(Boolean).length;

      if (availableCount > 1) {
        yield put(appActions.setPasswordResetAccountAvailability(availability));
        yield put(appActions.setLastTransactionState(TRANSACTION_STATE.ACCOUNT_CHOOSE));
        return;
      }

      if (availableCount === 1) {
        if (availability.has_narwhal_account) {
          yield call(requestNarwhalPasswordResetService, action.payload.userEmail, action.payload.locale || 'en-US');
          yield put(appActions.setLastTransactionState(TRANSACTION_STATE.SUCCESS));
        } else if (availability.has_personal_account) {
          yield call(requestServerPasswordReset, action.payload.userEmail, '', 'license_server', action.payload.locale);
          yield put(appActions.setLastTransactionState(TRANSACTION_STATE.SUCCESS));
        } else if (availability.has_business_account) {
          // Business accounts require a workspace; surface the workspace input
          yield put(appActions.errorKeyUpdated('error_workspace_required_to_use_username'));
          yield put(appActions.setLastTransactionState(TRANSACTION_STATE.ACCOUNT_CHOOSE));
        }
        return;
      }
    }

    if (requestResult.hasOwnProperty('is_both_personal_and_business')) {
      yield put(appActions.setIsBothPersonalAndBusiness(requestResult.is_both_personal_and_business));
      yield put(appActions.setLastTransactionState(TRANSACTION_STATE.ACCOUNT_CHOOSE));
      if (requestResult.is_both_personal_and_business === false) {
        yield put(appActions.errorKeyUpdated('error_workspace_required'));
      }
    } else {
      if (requestResult.message === 'org_not_allowed_password_change') {
        yield put(appActions.errorKeyUpdated('org_not_allowed_password_change'));
      }
      yield put(appActions.setLastTransactionState(TRANSACTION_STATE.SUCCESS));
    }
  } catch (error) {
    // JSON.parse(error.message) will throw an error if server doesn't send back JSON,
    // ie, if CORS error, error.message is `error: failed to fetch` - will throw when JSON.parse()
    let errorMsg = 'system_error_internal_service_error_(500)';
    try {
      let errorResponse = JSON.parse(error.message);
      if (errorResponse.message) {
        errorMsg = errorResponse.message;
      }
      yield put(appActions.errorKeyUpdated(errorMsg));
    } catch (e) {}
    yield put(appActions.setLastTransactionState(TRANSACTION_STATE.FAIL, errorMsg));
  }
}

function* requestPasswordResetOld(action) {
  try {
    yield call(emitEvent, RESET_PASSWORD, {
      Email: action.payload.userEmail,
      Application: 'Launchpad',
      'Application Version': '0.1.0'
    });
    yield put(appActions.setLastTransactionState(TRANSACTION_STATE.PENDING));
    const authToken = yield select(authSelectors.authToken);
    yield call(requestServerPasswordResetOld, action.payload.userEmail, authToken);
    yield put(appActions.setLastTransactionState(TRANSACTION_STATE.SUCCESS));
  } catch (error) {
    return yield put(appActions.setLastTransactionState(TRANSACTION_STATE.FAIL, JSON.parse(error.message).error));
  }
}

function* requestNarwhalPasswordReset(action) {
  try {
    yield call(emitEvent, RESET_PASSWORD, {
      Email: action.payload.email,
      Application: 'Launchpad - Narwhal',
      'Application Version': '0.1.0'
    });
    yield put(appActions.setLastTransactionState(TRANSACTION_STATE.PENDING));
    yield call(requestNarwhalPasswordResetService, action.payload.email, action.payload.locale);
    yield put(appActions.setLastTransactionState(TRANSACTION_STATE.SUCCESS));
  } catch (error) {
    yield put(appActions.setLastTransactionState(TRANSACTION_STATE.SUCCESS));
  }
}

function* loadPasswordModeSaga(action) {
  yield put(passwordChangeActions.storePasswordMode(''));
  const pwMode = yield call(loadPasswordMode, {
    userId: action.payload.userId,
    resetPasswordId: action.payload.resetPasswordId
  });
  const modeString = pwMode.strict ? 'strict' : 'simple';
  yield put(passwordChangeActions.storePasswordMode(modeString));
}

function* processPasswordReset(action) {
  try {
    if (action.payload.userType === null) {
      yield put(appActions.setLastTransactionState(TRANSACTION_STATE.PENDING));
      const authToken = yield select(authSelectors.authToken);
      yield call(
        processServerPasswordResetCatalyst,
        action.payload.resetRequestUuid,
        action.payload.newPassword,
        authToken
      );
      yield put(appActions.setLastTransactionState(TRANSACTION_STATE.SUCCESS));
      // this is to let the success screen appear for a bit
      yield delay(2500);
      yield put(push('/login'));
    } else if (action.payload.userType === 'consumer') {
      yield put(appActions.setLastTransactionState(TRANSACTION_STATE.PENDING));
      yield call(
        processServerPasswordResetConsumer,
        action.payload.key,
        action.payload.newPassword,
        action.payload.passwordConfirmation
      );
      yield put(appActions.setLastTransactionState(TRANSACTION_STATE.SUCCESS));
      // this is to let the success screen appear for a bit
      yield delay(2500);
      yield put(push('/login'));
    }
  } catch (error) {
    yield put(appActions.setLastTransactionState(TRANSACTION_STATE.FAIL, JSON.parse(error.message).error));
    const errMsg = JSON.parse(error.message).error;
    if (errMsg === 'REQUEST_INVALID') {
      yield put(push('/forgot-password'));
    }
  }
}

function* goToForgotPassword(action) {
  yield put(authActions.clearAuthError());
  yield put(passwordChangeActions.storeUserEmail(action.payload.userEmail));
  yield put(appActions.setLastTransactionState(TRANSACTION_STATE.NO_TRANSACTION));
  yield put(push('/old-forgot-password'));
}

function* goToForgotPasswordNew(action) {
  yield put(authActions.clearAuthError());
  yield put(passwordChangeActions.storeUserEmail(action.payload.userEmail));
  yield put(appActions.setLastTransactionState(TRANSACTION_STATE.NO_TRANSACTION));
  yield put(push('/forgot-password'));
}

export function* passwordChangeSagas() {
  yield takeLatest(LOAD_PASSWORD_MODE, loadPasswordModeSaga);
  yield takeLatest(REQUEST_PASSWORD_RESET, requestPasswordReset);
  yield takeLatest(REQUEST_PASSWORD_RESET_OLD, requestPasswordResetOld);
  yield takeLatest(REQUEST_NARWHAL_PASSWORD_RESET, requestNarwhalPasswordReset);
  yield takeLatest(PROCESS_PASSWORD_RESET, processPasswordReset);
  yield takeLatest(GO_TO_FORGOT_PASSWORD, goToForgotPassword);
  yield takeLatest(GO_TO_FORGOT_PASSWORD_NEW, goToForgotPasswordNew);
}



// WEBPACK FOOTER //
// ./src/modules/login/sagas/password-change-sagas.js